<template>
  <div class="skeleton-loader">
    <!-- Skeleton for card -->
    <b-card
      v-if="type === 'card'"
      class="skeleton-card"
    >
      <div class="skeleton-header">
        <div class="skeleton-circle" />
        <div class="skeleton-text-block">
          <div class="skeleton-text skeleton-text-short" />
          <div class="skeleton-text skeleton-text-medium" />
        </div>
      </div>
    </b-card>

    <!-- Skeleton for statistics -->
    <b-card
      v-else-if="type === 'statistics'"
      class="skeleton-card"
    >
      <div class="skeleton-stats">
        <div class="skeleton-circle-large" />
        <div class="skeleton-text-block">
          <div class="skeleton-text skeleton-text-large" />
          <div class="skeleton-text skeleton-text-medium" />
        </div>
      </div>
    </b-card>

    <!-- Skeleton for chart -->
    <b-card
      v-else-if="type === 'chart'"
      class="skeleton-card"
    >
      <div class="skeleton-chart-header">
        <div class="skeleton-text skeleton-text-medium" />
        <div class="skeleton-text skeleton-text-short" />
      </div>
      <div class="skeleton-chart">
        <div
          class="skeleton-bar"
          style="height: 60%"
        />
        <div
          class="skeleton-bar"
          style="height: 80%"
        />
        <div
          class="skeleton-bar"
          style="height: 40%"
        />
        <div
          class="skeleton-bar"
          style="height: 90%"
        />
        <div
          class="skeleton-bar"
          style="height: 70%"
        />
        <div
          class="skeleton-bar"
          style="height: 50%"
        />
      </div>
    </b-card>

    <!-- Skeleton for table -->
    <b-card
      v-else-if="type === 'table'"
      class="skeleton-card"
    >
      <div class="skeleton-table-header">
        <div class="skeleton-text skeleton-text-medium" />
      </div>
      <div class="skeleton-table">
        <div
          v-for="i in rows"
          :key="i"
          class="skeleton-table-row"
        >
          <div class="skeleton-text skeleton-text-long" />
          <div class="skeleton-text skeleton-text-short" />
          <div class="skeleton-text skeleton-text-medium" />
        </div>
      </div>
    </b-card>

    <!-- Skeleton for transactions -->
    <b-card
      v-else-if="type === 'transactions'"
      class="skeleton-card"
    >
      <div class="skeleton-transaction-header">
        <div class="skeleton-text skeleton-text-medium" />
      </div>
      <div
        v-for="i in 4"
        :key="i"
        class="skeleton-transaction-item"
      >
        <div class="skeleton-circle" />
        <div class="skeleton-text-block flex-grow-1">
          <div class="skeleton-text skeleton-text-medium" />
          <div class="skeleton-text skeleton-text-short" />
        </div>
        <div class="skeleton-text skeleton-text-short" />
      </div>
    </b-card>

    <!-- Skeleton for financial statistics -->
    <b-card
      v-else-if="type === 'financial-stats'"
      class="skeleton-card"
    >
      <div class="skeleton-financial-stats">
        <div
          v-for="i in 4"
          :key="i"
          class="skeleton-financial-item"
        >
          <div class="skeleton-circle" />
          <div class="skeleton-text-block">
            <div class="skeleton-text skeleton-text-large" />
            <div class="skeleton-text skeleton-text-short" />
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard } from 'bootstrap-vue'

export default {
  name: 'SkeletonLoader',
  components: {
    BCard,
  },
  props: {
    type: {
      type: String,
      default: 'card',
      validator: value => ['card', 'statistics', 'chart', 'table', 'transactions', 'financial-stats'].includes(value),
    },
    rows: {
      type: Number,
      default: 5,
    },
  },
}
</script>

<style lang="scss" scoped>
.skeleton-loader {
  width: 100%;
}

.skeleton-card {
  padding: 1rem;
  border-radius: 0.428rem;
}

// Animation
@keyframes shimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.skeleton-text,
.skeleton-circle,
.skeleton-circle-large,
.skeleton-bar {
  background: linear-gradient(to right, #eff1f3 4%, #e2e5e7 25%, #eff1f3 36%);
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
  border-radius: 4px;
}

.skeleton-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.skeleton-circle-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
}

.skeleton-text {
  height: 16px;
  margin-bottom: 8px;
  border-radius: 4px;
}

.skeleton-text-short {
  width: 40%;
}

.skeleton-text-medium {
  width: 60%;
}

.skeleton-text-long {
  width: 80%;
}

.skeleton-text-large {
  height: 24px;
  width: 50%;
}

.skeleton-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.skeleton-text-block {
  flex: 1;
}

.skeleton-stats {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 0;
}

.skeleton-chart-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.skeleton-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 200px;
  gap: 10px;
  padding: 0 1rem;
}

.skeleton-bar {
  flex: 1;
  background: linear-gradient(to right, #eff1f3 4%, #e2e5e7 25%, #eff1f3 36%);
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
  border-radius: 4px 4px 0 0;
}

.skeleton-table-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eff1f3;
}

.skeleton-table-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f8f8;
}

.skeleton-transaction-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eff1f3;
}

.skeleton-transaction-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
}

.skeleton-financial-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.skeleton-financial-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 200px;
}

// Dark mode support
.dark-layout {
  .skeleton-text,
  .skeleton-circle,
  .skeleton-circle-large,
  .skeleton-bar {
    background: linear-gradient(to right, #283046 4%, #3b4253 25%, #283046 36%);
    background-size: 1000px 100%;
  }

  .skeleton-table-row,
  .skeleton-transaction-item {
    border-bottom-color: #3b4253;
  }

  .skeleton-table-header,
  .skeleton-transaction-header {
    border-bottom-color: #3b4253;
  }
}
</style>
